import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Command } from 'cmdk'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import {
  Cloud,
  CornerDownLeft,
  Fingerprint,
  History,
  ShieldAlert,
  Sparkles,
  TriangleAlert,
  User,
} from 'lucide-react'
import { apiFetch } from '@/api/client'
import type {
  AnalysisRecord,
  AuditEventsPage,
  CloudEnvironment,
  FindingWire,
  FindingsWirePage,
  IdentitiesPage,
} from '@/domain'
import { parseAnalysis } from '@/domain'
import { ALL_NAV_ITEMS } from './nav-config'
import { getRecentPages, type RecentPage } from './recent-pages'
import { ensureShellStyles } from './shell-css'

/**
 * Global ⌘K command palette (cmdk). Navigates to every route, recalls recent
 * pages, and searches these entity planes:
 *  - Identities — the directory projection (IdentitiesPage envelope), searched
 *    server-side via the `search` param (get_audit/captures-derived).
 *  - Principals — audit-event actors via get_audit_events_paged's `q` (ILIKE on
 *    principal_name/id); Enter lands on that principal's access-trail timeline.
 *  - Analyses   — the only severity the backend emits (LLM assessment text,
 *    parsed by parseAnalysis).
 *  - Findings   — the derived read-only projection (/api/v1/findings), matched
 *    on title client-side; Enter opens that finding's drawer (?finding=).
 * Each plane fails independently: a group that errors shows an inline error row
 * rather than silently disappearing.
 */

ensureShellStyles()

export interface SearchCommandProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const MAX_RESULTS = 6
const SEARCH_MIN_CHARS = 2
/** Cap concurrent per-env analysis fetches; the org has a handful of envs. */
const ANALYSES_ENV_CAP = 8

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(id)
  }, [value, delayMs])
  return debounced
}

function matches(haystack: string | null | undefined, needle: string): boolean {
  return haystack != null && haystack.toLowerCase().includes(needle)
}

function Row({
  icon,
  label,
  meta,
}: {
  icon: ReactNode
  label: string
  meta?: string
}) {
  return (
    <>
      <span style={{ color: 'var(--text-mute)', display: 'inline-flex' }}>{icon}</span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      {meta && (
        <span
          style={{
            fontSize: 11,
            color: 'var(--text-faint)',
            fontFamily: 'var(--ff-mono)',
            flexShrink: 0,
          }}
        >
          {meta}
        </span>
      )}
    </>
  )
}

/** Inline per-group failure row — a group never silently vanishes on error. */
function GroupError({ heading, label }: { heading: string; label: string }) {
  return (
    <Command.Group heading={heading}>
      <div
        role="alert"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          fontSize: 12,
          color: 'var(--err-500)',
        }}
      >
        <TriangleAlert size={13} aria-hidden />
        {label}
      </div>
    </Command.Group>
  )
}

export function SearchCommand({ open, onOpenChange }: SearchCommandProps) {
  const router = useRouter()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 300)

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const q = debouncedQuery.trim()
  const qLower = q.toLowerCase()
  const searching = q.length >= SEARCH_MIN_CHARS

  // Identities — directory envelope ({items,…}); search server-side so we hit
  // the whole directory, not just the default first page filtered client-side.
  const identitiesQ = useQuery({
    queryKey: ['palette', 'identities', q],
    queryFn: () =>
      apiFetch<IdentitiesPage>({
        url: '/api/v1/identities',
        params: { search: q, limit: MAX_RESULTS },
      }),
    enabled: open && searching,
    retry: false,
    staleTime: 30_000,
  })

  // Principals — audit-event actors via the paged events `q` (ILIKE on
  // principal_name / principal_id). Deduped to one row per principal.
  const principalsQ = useQuery({
    queryKey: ['palette', 'principals', q],
    queryFn: () =>
      apiFetch<AuditEventsPage>({
        url: '/api/v1/audit',
        params: { q, limit: 100 },
      }),
    enabled: open && searching,
    retry: false,
    staleTime: 30_000,
  })

  // Environments — fetched once per open; filtered client-side on name + scopeId
  // (scopeId is the AWS account / GCP project / Azure subscription, derivable).
  const envsQ = useQuery({
    queryKey: ['palette', 'envs'],
    queryFn: () => apiFetch<CloudEnvironment[]>({ url: '/api/v1/envs' }),
    enabled: open,
    retry: false,
    staleTime: 60_000,
  })

  // Analyses — the real backend assessment. No org-wide endpoint exists, so we
  // fan out across the loaded envs (capped) to the per-env analyses route and
  // parse the mandated Severity Assessment section client-side.
  const envIds = useMemo(
    () => (Array.isArray(envsQ.data) ? envsQ.data.slice(0, ANALYSES_ENV_CAP).map((e) => e.id) : []),
    [envsQ.data],
  )
  const analysesQ = useQuery({
    queryKey: ['palette', 'analyses', envIds],
    queryFn: async () => {
      const perEnv = await Promise.all(
        envIds.map((id) =>
          apiFetch<AnalysisRecord[]>({ url: `/api/v1/envs/${id}/analyses` }),
        ),
      )
      return perEnv.flat()
    },
    enabled: open && envIds.length > 0,
    retry: false,
    staleTime: 60_000,
  })

  // Findings — the derived read-only projection (/api/v1/findings). Consumes
  // the raw wire shape directly (id: number) — this palette only ever reads
  // id/title/severity and stringifies id for the URL, so it has no need for
  // the client-normalized `Finding` the findings page adapts to. No title
  // search on the endpoint, so fetch a page once and match titles
  // client-side (the feed is a few dozen rows).
  const findingsQ = useQuery({
    queryKey: ['palette', 'findings'],
    queryFn: () =>
      apiFetch<FindingsWirePage>({ url: '/api/v1/findings', params: { limit: 100 } }),
    enabled: open && searching,
    retry: false,
    staleTime: 60_000,
  })

  const navResults = useMemo(
    () => ALL_NAV_ITEMS.filter((item) => qLower === '' || matches(item.label, qLower)),
    [qLower],
  )
  const recentResults = useMemo(
    (): readonly RecentPage[] => (qLower === '' ? getRecentPages() : []),
    [qLower],
  )

  const identityResults = useMemo(
    () => (searching ? (identitiesQ.data?.items ?? []).slice(0, MAX_RESULTS) : []),
    [searching, identitiesQ.data],
  )

  const principalResults = useMemo(() => {
    if (!searching) return []
    const seen = new Set<string>()
    const out: AuditEventsPage['items'] = []
    for (const ev of principalsQ.data?.items ?? []) {
      if (seen.has(ev.principalName)) continue
      seen.add(ev.principalName)
      out.push(ev)
      if (out.length >= MAX_RESULTS) break
    }
    return out
  }, [searching, principalsQ.data])

  const envResults = useMemo(() => {
    if (!searching) return []
    const all = Array.isArray(envsQ.data) ? envsQ.data : []
    return all
      .filter((e) => matches(e.name, qLower) || matches(e.scopeId, qLower))
      .slice(0, MAX_RESULTS)
  }, [searching, qLower, envsQ.data])

  const analysisResults = useMemo(() => {
    if (!searching) return []
    return (analysesQ.data ?? [])
      .filter((a) => matches(a.envName, qLower) || matches(parseAnalysis(a).summary, qLower))
      .slice(0, MAX_RESULTS)
  }, [searching, qLower, analysesQ.data])

  const findingResults = useMemo((): readonly FindingWire[] => {
    if (!searching) return []
    return (findingsQ.data?.items ?? [])
      .filter((f) => matches(f.title, qLower))
      .slice(0, MAX_RESULTS)
  }, [searching, qLower, findingsQ.data])

  const go = (path: string) => {
    onOpenChange(false)
    router.history.push(path)
  }

  const loading =
    searching &&
    (identitiesQ.isLoading ||
      principalsQ.isLoading ||
      envsQ.isLoading ||
      analysesQ.isLoading ||
      findingsQ.isLoading)

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      shouldFilter={false}
    >
      <Command.Input
        value={query}
        onValueChange={setQuery}
        placeholder="Search pages, identities, principals, environments…"
      />
      <Command.List>
        <Command.Empty>{loading ? 'Searching…' : 'No results found.'}</Command.Empty>
        {recentResults.length > 0 && (
          <Command.Group heading="Recent">
            {recentResults.map(({ path, label }) => (
              <Command.Item key={`recent:${path}`} value={`recent:${path}`} onSelect={() => go(path)}>
                <Row icon={<History size={14} aria-hidden />} label={label} meta={path} />
              </Command.Item>
            ))}
          </Command.Group>
        )}
        {navResults.length > 0 && (
          <Command.Group heading="Pages">
            {navResults.map((item) => {
              const Icon = item.icon
              return (
                <Command.Item
                  key={`page:${item.to}`}
                  value={`page:${item.to}`}
                  onSelect={() => go(item.to)}
                >
                  <Row icon={<Icon size={14} aria-hidden />} label={item.label} meta={item.to} />
                </Command.Item>
              )
            })}
          </Command.Group>
        )}

        {searching && identitiesQ.isError && (
          <GroupError heading="Identities" label="Identity directory unavailable" />
        )}
        {identityResults.length > 0 && (
          <Command.Group heading="Identities">
            {identityResults.map((identity) => (
              <Command.Item
                key={`identity:${identity.id}`}
                value={`identity:${identity.id}`}
                onSelect={() => go(`/identities?id=${encodeURIComponent(identity.id)}`)}
              >
                <Row
                  icon={<User size={14} aria-hidden />}
                  label={identity.name}
                  meta={`${identity.type.replace('_', ' ')} · ${identity.provider.toUpperCase()}`}
                />
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {searching && principalsQ.isError && (
          <GroupError heading="Principals" label="Access-trail search unavailable" />
        )}
        {principalResults.length > 0 && (
          <Command.Group heading="Principals">
            {principalResults.map((ev) => (
              <Command.Item
                key={`principal:${ev.principalId}`}
                value={`principal:${ev.principalId}`}
                onSelect={() =>
                  go(`/access-trail?q=${encodeURIComponent(ev.principalName)}`)
                }
              >
                <Row
                  icon={<Fingerprint size={14} aria-hidden />}
                  label={ev.principalName}
                  meta={`${ev.principalType.replace('_', ' ')} · ${ev.provider}`}
                />
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {searching && analysesQ.isError && (
          <GroupError heading="Analyses" label="Assessments unavailable" />
        )}
        {analysisResults.length > 0 && (
          <Command.Group heading="Analyses">
            {analysisResults.map((a) => (
              <Command.Item
                key={`analysis:${a.id}`}
                value={`analysis:${a.id}`}
                onSelect={() => go(`/analyze?id=${encodeURIComponent(a.id)}`)}
              >
                <Row
                  icon={<Sparkles size={14} aria-hidden />}
                  label={a.envName}
                  meta={parseAnalysis(a).severity}
                />
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {searching && findingsQ.isError && (
          <GroupError heading="Findings" label="Findings unavailable" />
        )}
        {findingResults.length > 0 && (
          <Command.Group heading="Findings">
            {findingResults.map((f) => {
              // Stringify the wire's numeric id — the findings page's ?finding=
              // lookup keys its rows by the same String(wire.id) (use-findings.ts).
              const id = String(f.id)
              return (
                <Command.Item
                  key={`finding:${id}`}
                  value={`finding:${id}`}
                  onSelect={() => go(`/findings?finding=${encodeURIComponent(id)}`)}
                >
                  <Row
                    icon={<ShieldAlert size={14} aria-hidden />}
                    label={f.title}
                    meta={f.severity}
                  />
                </Command.Item>
              )
            })}
          </Command.Group>
        )}

        {searching && envsQ.isError && (
          <GroupError heading="Environments" label="Environments unavailable" />
        )}
        {envResults.length > 0 && (
          <Command.Group heading="Environments">
            {envResults.map((env) => (
              <Command.Item
                key={`env:${env.id}`}
                value={`env:${env.id}`}
                onSelect={() => go(`/envs/${env.id}`)}
              >
                <Row
                  icon={<Cloud size={14} aria-hidden />}
                  label={env.name}
                  meta={env.scopeId ?? env.provider}
                />
              </Command.Item>
            ))}
          </Command.Group>
        )}
      </Command.List>
      <div
        aria-hidden
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 14px',
          borderTop: '1px solid var(--border)',
          fontSize: 11,
          color: 'var(--text-faint)',
        }}
      >
        <CornerDownLeft size={11} />
        to open
        <span style={{ marginLeft: 'auto', fontFamily: 'var(--ff-mono)' }}>esc to close</span>
      </div>
    </Command.Dialog>
  )
}
